import { useCallback, useEffect, useMemo, useState } from "react";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { FieldGroup, Input, Textarea } from "../../components/ui";
import { teamApi } from "../../services/apiClient";
import { formatDateTimeFr } from "../../shared/dateFormat";
import { COLORS, RADIUS } from "../../shared/theme";

function StatCard({ label, value, color, bg, border }) {
  return (
    <div style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: RADIUS.lg, padding: "16px 18px" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function StatusPill({ label, tone = "neutral" }) {
  const palette =
    tone === "success"
      ? { bg: "#ecfdf3", border: "#bbf7d0", color: "#065f46" }
      : tone === "warning"
        ? { bg: "#fffbeb", border: "#fcd34d", color: "#92400e" }
        : tone === "danger"
          ? { bg: "#fef2f2", border: "#fecaca", color: "#b91c1c" }
          : { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "5px 10px",
        borderRadius: 999,
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        color: palette.color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

function toneForInvitation(item) {
  if (item.status === "accepted_existing" || item.status === "accepted_new") return "success";
  if (item.status === "revoked" || item.isExpired) return "danger";
  return "warning";
}

export default function PageTeamConfig({ onToast, onBack }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [mail, setMail] = useState(null);
  const [form, setForm] = useState({ email: "", note: "" });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await teamApi.dashboard();
      setMembers(data.members || []);
      setInvitations(data.invitations || []);
      setMail(data.mail || null);
    } catch (e) {
      setError(e.message || "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pendingCount = useMemo(() => invitations.filter((item) => item.status === "pending" && !item.isExpired).length, [invitations]);

  const invite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = await teamApi.invite(form);
      setMembers(data.members || []);
      setInvitations(data.invitations || []);
      setMail(data.mail || null);
      setForm({ email: "", note: "" });
      onToast?.(data.message || "Invitation envoyee.");
    } catch (err) {
      setError(err.message || "Invitation impossible.");
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (id) => {
    setActionId(id);
    setError(null);
    try {
      const data = await teamApi.resend(id);
      setMembers(data.members || []);
      setInvitations(data.invitations || []);
      setMail(data.mail || null);
      onToast?.(data.message || "Invitation renvoyee.");
    } catch (err) {
      setError(err.message || "Renvoi impossible.");
    } finally {
      setActionId(null);
    }
  };

  const revoke = async (id) => {
    setActionId(id);
    setError(null);
    try {
      const data = await teamApi.revoke(id);
      setMembers(data.members || []);
      setInvitations(data.invitations || []);
      setMail(data.mail || null);
      onToast?.(data.message || "Invitation revoquee.");
    } catch (err) {
      setError(err.message || "Revocation impossible.");
    } finally {
      setActionId(null);
    }
  };

  const mailHealthy = Boolean(mail?.fromAddress && mail?.mailConfigured && mail?.frontendConfigured);
  const mailModeLabel = mail?.mailer === "smtp" ? "SMTP" : (mail?.mailer || "-").toUpperCase();

  return (
    <PageShell>
      <PageHeader
        title="Equipe & invitations"
        subtitle="Invitez des collaborateurs et suivez les acces a votre tenant."
        onBack={onBack}
      />

      <PageContent>
        <div style={{ display: "grid", gap: 18 }}>
        {error && (
          <div style={{ padding: "12px 14px", background: "#fef2f2", color: "#b91c1c", borderRadius: RADIUS.md, border: "1.5px solid #fecaca" }}>
            {error}
          </div>
        )}

        <div style={{ padding: "16px 18px", background: mailHealthy ? "#ecfdf3" : "#fffbeb", color: mailHealthy ? "#065f46" : "#92400e", borderRadius: RADIUS.lg, border: `1.5px solid ${mailHealthy ? "#bbf7d0" : "#fcd34d"}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
                {mailHealthy ? "Configuration email prete" : "Verifier la configuration email"}
              </div>
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                Mode: <strong>{mailModeLabel}</strong>
                {mail?.mailer === "smtp" && mail?.smtpHost ? <> | Hote SMTP: <strong>{mail.smtpHost}:{mail.smtpPort || "-"}</strong></> : null}
                {" "} | From: <strong>{mail?.fromName || "-"}</strong> &lt;{mail?.fromAddress || "-"}&gt; | Frontend: <strong>{mail?.frontendUrl || "-"}</strong> | Expiration: <strong>{mail?.expirationDays || 7} jours</strong>
              </div>
            </div>
            <StatusPill label={mailHealthy ? "Email configure" : "Configuration incomplete"} tone={mailHealthy ? "success" : "warning"} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
          <StatCard label="Membres actifs" value={members.length} color="#1d4ed8" bg="#eff6ff" border="#bfdbfe" />
          <StatCard label="Invitations en attente" value={pendingCount} color="#92400e" bg="#fffbeb" border="#fcd34d" />
          <StatCard label="Historique invitations" value={invitations.length} color="#065f46" bg="#ecfdf3" border="#bbf7d0" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 420px) 1fr", gap: 18, alignItems: "start" }}>
          <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 22 }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>Inviter un membre</div>
            <div style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 16 }}>
              Si le compte existe deja, l’utilisateur est rattache automatiquement au tenant. Sinon, il recoit un lien securise pour creer son acces.
            </div>

            <form onSubmit={invite}>
              <FieldGroup label="Email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                  placeholder="prenom.nom@entreprise.com"
                />
              </FieldGroup>
              <FieldGroup label="Message optionnel">
                <Textarea
                  value={form.note}
                  onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                  rows={4}
                  placeholder="Ajoutez un contexte pour le collaborateur..."
                />
              </FieldGroup>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: "9px 18px",
                  borderRadius: RADIUS.md,
                  border: "none",
                  background: "#0ea5e9",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: submitting ? "wait" : "pointer",
                  opacity: submitting ? 0.8 : 1,
                }}
              >
                {submitting ? "Envoi..." : "Envoyer l'invitation"}
              </button>
            </form>
          </div>

          <div style={{ display: "grid", gap: 18 }}>
            <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 22 }}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>Membres actifs</div>
              {loading ? (
                <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Chargement...</div>
              ) : members.length === 0 ? (
                <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Aucun membre rattache pour le moment.</div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {members.map((member) => (
                    <div key={member.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: "14px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>{member.email}</div>
                          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4 }}>
                            Rattache le {member.createdAt ? formatDateTimeFr(member.createdAt) : "-"}{member.invitedAt ? ` | invite le ${formatDateTimeFr(member.invitedAt)}` : ""}
                          </div>
                        </div>
                        <StatusPill label={member.statusLabel} tone="success" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ background: COLORS.white, borderRadius: RADIUS.xl, border: `1.5px solid ${COLORS.border}`, padding: 22 }}>
              <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 12 }}>Invitations</div>
              {loading ? (
                <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Chargement...</div>
              ) : invitations.length === 0 ? (
                <div style={{ fontSize: 13, color: COLORS.textSecondary }}>Aucune invitation envoyee.</div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {invitations.map((item) => (
                    <div key={item.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.lg, padding: "14px 16px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                        <div style={{ flex: 1, minWidth: 220 }}>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>{item.email}</div>
                          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 4, lineHeight: 1.6 }}>
                            Creee le {item.createdAt ? formatDateTimeFr(item.createdAt) : "-"}
                            {item.lastSentAt ? ` | dernier envoi ${formatDateTimeFr(item.lastSentAt)}` : ""}
                            {item.expiresAt ? ` | expire le ${formatDateTimeFr(item.expiresAt)}` : ""}
                          </div>
                          {item.note && <div style={{ marginTop: 8, fontSize: 12, color: COLORS.textSecondary }}>{item.note}</div>}
                        </div>
                        <StatusPill label={item.statusLabel} tone={toneForInvitation(item)} />
                      </div>
                      {(item.canResend || item.canRevoke) && (
                        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                          {item.canResend && (
                            <button
                              type="button"
                              onClick={() => resend(item.id)}
                              disabled={actionId === item.id}
                              style={{ padding: "8px 12px", borderRadius: RADIUS.md, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: "pointer", fontWeight: 700 }}
                            >
                              {actionId === item.id ? "..." : "Renvoyer"}
                            </button>
                          )}
                          {item.canRevoke && (
                            <button
                              type="button"
                              onClick={() => revoke(item.id)}
                              disabled={actionId === item.id}
                              style={{ padding: "8px 12px", borderRadius: RADIUS.md, border: "1px solid #fecaca", background: "#fff1f2", color: "#b91c1c", cursor: "pointer", fontWeight: 700 }}
                            >
                              Revoquer
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </PageContent>
    </PageShell>
  );
}
